<template>
	<div class="supplier-access-panel">
		<!-- SUPPLIER DOCS -------------------------------------------------- -->
		<section class="panel-card supplier-section supplier-documents-section">
			<header class="panel-card__header">
				<h3 class="panel-card__title">
					<button
						type="button"
						class="documents-toggle"
						:aria-expanded="documentsExpanded"
						:aria-controls="`supplier-documents-${supplierKey}`"
						@click="documentsExpanded = !documentsExpanded"
					>
						<span class="documents-toggle__label">Supplier docs</span>
						<BsChevronDown class="documents-toggle__chevron" :class="{ open: documentsExpanded }" aria-hidden="true" />
					</button>
				</h3>
			</header>

			<div
				v-show="documentsExpanded"
				:id="`supplier-documents-${supplierKey}`"
				:hidden="!documentsExpanded"
				class="panel-card__body"
			>
				<p class="sr-only" aria-live="polite">{{ documentStatusMessage }}</p>
				<div v-if="needsSigningProfile" class="notice">
					<div class="notice__body">
						<p class="notice__title">Signing profile required</p>
						<p class="notice__hint">
							Complete your profile before sending owner OTP codes for bilateral documents.
						</p>
					</div>
					<button
						type="button"
						class="secondary-btn"
						@click="openSigningProfileModal"
						:disabled="isBusy"
					>
						Set up
					</button>
				</div>

				<p v-if="docsError" class="inline-error" role="alert">{{ docsError }}</p>
				<p v-if="actionError" class="inline-error" role="alert">{{ actionError }}</p>

				<div class="document-groups">
					<section
						v-for="group in documentGroups"
						:key="group.value"
						class="document-group"
						:data-category="group.value"
						:aria-labelledby="`supplier-doc-group-${group.value}`"
					>
						<header class="document-group__header">
							<div class="document-group__identity">
								<h4 :id="`supplier-doc-group-${group.value}`" class="document-group__title">
									{{ group.label }}
								</h4>
							</div>

							<div class="assign-row" :class="{ 'assign-row--selected': !!attachDocumentIds[group.value] }">
								<label :for="`assignDocument-${group.value}`" class="sr-only">
									Assign a document in {{ group.label }}
								</label>
								<div class="assign-row__select">
									<AppointmentIcon name="plus" :size="16" class="assign-row__plus" />
									<CatalogSearchInput
										:input-id="`assignDocument-${group.value}`"
										:ref="(component) => setAssignSelectRef(group.value, component)"
										v-model="attachDocumentIds[group.value]"
										:options="group.selectOptions"
										:placeholder="group.selectableDocuments.length ? group.placeholder : group.unavailableLabel"
										:data-test="`assign-select-${group.value}`"
										:disabled="isBusy || disabled || !group.selectableDocuments.length"
										selection-only
										use-fixed
									/>
								</div>
								<button
									type="button"
									class="document-action-btn assign-row__btn"
									:data-test="`assign-button-${group.value}`"
									@click="attachSelected(group.value)"
									:disabled="isBusy || disabled || !attachDocumentIds[group.value]"
								>
									<AppointmentIcon name="assign" :size="16" />
									<span>Assign</span>
								</button>
							</div>
						</header>

						<ul v-if="group.documents.length" class="doc-list">
							<li
								v-for="doc in group.documents"
								:key="doc.id"
								class="doc-item"
								:class="{ 'doc-item--expanded': isDocumentExpanded(doc) }"
								:data-document-id="String(doc.id)"
							>
								<button
									type="button"
									:ref="(element) => setDocumentToggleRef(doc, element)"
									class="doc-item__toggle"
									:aria-expanded="isDocumentExpanded(doc)"
									:aria-controls="documentDetailsId(doc)"
									@click="toggleDocument(doc)"
								>
									<span
										class="doc-item__name"
										:class="{ 'doc-item__name--with-reference': getDocumentReference(doc) }"
									>
										{{ getDocumentLabel(doc) }}
									</span>
									<BsChevronDown
										class="doc-item__chevron"
										:class="{ open: isDocumentExpanded(doc) }"
										aria-hidden="true"
									/>
								</button>

								<transition name="doc-details">
									<div
										v-show="isDocumentExpanded(doc)"
										:id="documentDetailsId(doc)"
										class="doc-item__details"
										:data-test="`doc-details-${doc.id}`"
										role="region"
										:aria-label="`${getDocumentLabel(doc)} details`"
										:aria-hidden="!isDocumentExpanded(doc)"
										:hidden="!isDocumentExpanded(doc)"
									>
										<div class="doc-item__summary">
											<span class="badge" :class="docBadgeClass(getDocState(doc))">
												{{ docStateLabel(getDocState(doc)) }}
											</span>
										</div>

										<dl class="doc-item__activity">
											<div class="doc-item__meta-entry">
												<dt>Viewed</dt>
												<dd :class="{ 'is-pending': !doc.activity?.viewedAt }">
													{{ formatDocumentEvent(doc.activity?.viewedAt) }}
												</dd>
											</div>
											<div class="doc-item__meta-entry">
												<dt>Downloaded</dt>
												<dd :class="{ 'is-pending': !doc.activity?.downloadedAt }">
													{{ formatDocumentEvent(doc.activity?.downloadedAt) }}
												</dd>
											</div>
											<template v-if="doc.completionMode === 'bilateral_signature'">
												<div class="doc-item__meta-entry">
													<dt>Effective date</dt>
													<dd :class="{ 'is-pending': !doc.effectiveDate }">
														{{ formatDocumentEvent(doc.effectiveDate, { dateOnly: true }) }}
													</dd>
												</div>
												<div class="doc-item__meta-entry">
													<dt>Owner signed</dt>
													<dd :class="{ 'is-pending': !doc.execution?.ownerSignedAt }">
														{{ formatDocumentEvent(doc.execution?.ownerSignedAt) }}
													</dd>
												</div>
												<div class="doc-item__meta-entry">
													<dt>Supplier signed</dt>
													<dd :class="{ 'is-pending': !doc.execution?.supplierSignedAt }">
														{{ formatDocumentEvent(doc.execution?.supplierSignedAt) }}
													</dd>
												</div>
											</template>
										</dl>

										<div v-if="hasSigningSection(doc)" class="doc-item__signing">
											<div v-if="canPrepareOwnerSignature(doc)" class="signing-controls">
												<div class="signing-field">
													<label class="signing-field__label" :for="`owner-effective-date-${doc.id}`">Effective date</label>
													<DateRangePicker
														:id="`owner-effective-date-${doc.id}`"
														mode="single"
														use-fixed
														:glass="false"
														class="signing-date-picker"
														:date-from="getEffectiveDateValue(doc)"
														:disabled="isBusy || disabled || isDocBusy(doc)"
														trigger-label="Effective date"
														placeholder="Select date"
														@update="({ dateFrom }) => setEffectiveDateValue(doc, dateFrom)"
													>
														<template #trigger-icon><AppointmentIcon name="calendar" :size="16" /></template>
														<template #previous-icon><AppointmentIcon name="chevron-left" :size="16" /></template>
														<template #next-icon><AppointmentIcon name="chevron-right" :size="16" /></template>
													</DateRangePicker>
												</div>
												<button
													type="button"
													class="secondary-btn"
													@click="requestOwnerOtp(doc)"
													:disabled="isBusy || disabled || isDocBusy(doc)"
												>
													Send OTP
												</button>
											</div>

											<div v-if="hasOwnerOtpPending(doc)" class="signing-controls">
												<label class="signing-field" :for="`owner-otp-code-${doc.id}`">
													<span class="signing-field__label">Owner OTP code</span>
													<input
														:id="`owner-otp-code-${doc.id}`"
														:value="getOwnerOtpCode(doc)"
														type="text"
														class="input signing-input"
														placeholder="Enter OTP code"
														@input="setOwnerOtpCode(doc, $event.target.value)"
													/>
												</label>
												<button
													type="button"
													class="secondary-btn"
													@click="confirmOwnerSignature(doc)"
													:disabled="isBusy || disabled || !getOwnerOtpCode(doc) || isDocBusy(doc)"
												>
													Sign as owner
												</button>
											</div>
										</div>

										<div class="doc-item__actions">
											<button
												v-if="doc.completionMode === 'bilateral_signature' && doc.executionStatus === 'completed' && doc.executionId"
												type="button"
												class="link-btn"
												@click="downloadExecutedCopy(doc)"
												:disabled="isBusy || disabled || isDocBusy(doc)"
											>
												Download signed PDF
											</button>
											<button
												type="button"
												class="link-btn"
												:disabled="disabled || openingId === `shared-${doc.id}`"
												@click="openSharedDocument(doc, `shared-${doc.id}`)"
											>
												{{ getAssignmentDownloadFilename(doc) ? 'Download document' : 'Open document' }}
											</button>
											<button
												type="button"
												class="link-btn link-btn--danger"
												:disabled="disabled || isDocBusy(doc)"
												@click="requestUnassignSharedDocument(doc, $event)"
											>
												Remove assignment
											</button>
										</div>
									</div>
								</transition>
							</li>
						</ul>
						<p v-else class="document-group__empty">
							{{ docsLoading ? 'Loading documents…' : group.emptyLabel }}
						</p>
					</section>
				</div>
			</div>
		</section>

		<!-- ATTACHMENTS --------------------------------------------------- -->
		<section class="panel-card supplier-section supplier-attachments-section">
			<header class="panel-card__header">
				<h3 class="panel-card__title">
					<button
						type="button"
						class="documents-toggle"
						:aria-expanded="attachmentsExpanded"
						:aria-controls="`supplier-attachments-${supplierKey}`"
						@click="attachmentsExpanded = !attachmentsExpanded"
					>
						<span class="documents-toggle__label">Attachments</span>
						<BsChevronDown class="documents-toggle__chevron" :class="{ open: attachmentsExpanded }" aria-hidden="true" />
					</button>
				</h3>
				<button
					type="button"
					class="document-action-btn"
					:disabled="disabled"
					@click="openFilePicker"
				>
					<AppointmentIcon name="export" :size="16" />
					Upload
				</button>
			</header>

			<div
				v-show="attachmentsExpanded"
				:id="`supplier-attachments-${supplierKey}`"
				:hidden="!attachmentsExpanded"
				class="panel-card__body"
			>
				<input
					ref="fileInput"
					type="file"
					class="file-input"
					multiple
					:disabled="disabled"
					@change="onFileChange"
				/>

				<ul v-if="visibleAttachments.length" class="file-list">
					<li
						v-for="attachment in visibleAttachments"
						:key="`uploaded-${attachment.id || attachment.localId}`"
						class="file-item"
					>
						<div class="file-item__main">
							<span class="file-item__icon" aria-hidden="true">
								<BsFileEarmarkText />
							</span>
							<span class="file-item__name">{{ attachment.name || 'Untitled file' }}</span>
						</div>
						<div class="file-item__actions">
							<button
								v-if="analysisEnabled && isRightToWorkAttachment(attachment)"
								type="button"
								class="link-btn link-btn--ai"
								:title="analysisButtonTitle(attachment)"
								:disabled="disabled || !canAnalyzeAttachment(attachment) || isAnalyzingAttachment(attachment)"
								@click="$emit('analyze-attachment', attachment)"
							>
								{{ isAnalyzingAttachment(attachment) ? 'Reading…' : 'Read with AI' }}
							</button>
							<button
								type="button"
								class="link-btn"
								:disabled="disabled || openingId === `uploaded-${attachment.id || attachment.localId}`"
								@click="openUploadedAttachment(attachment, `uploaded-${attachment.id || attachment.localId}`)"
							>
								View
							</button>
							<button
								type="button"
								class="link-btn link-btn--danger"
								:disabled="disabled"
								@click="removeUploadedAttachment(attachment)"
							>
								Remove
							</button>
						</div>
					</li>
				</ul>
				<p v-else class="attachments-empty">No attachments yet.</p>
			</div>
		</section>

		<div
			v-if="unassignTarget"
			class="modal-overlay"
			@click.self="closeUnassignConfirmation"
		>
			<section
				ref="unassignDialogRef"
				class="modal-content unassign-confirm-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="remove-assignment-title"
				tabindex="-1"
				@keydown.esc.prevent="closeUnassignConfirmation"
				@keydown.tab="trapUnassignModalFocus"
			>
				<h3 id="remove-assignment-title" class="modal-title">Remove assignment</h3>
				<p class="modal-message">
					Remove assignment of <strong>{{ getDocumentLabel(unassignTarget) }}</strong>?
					The source document will not be deleted.
				</p>
				<p v-if="unassignError" class="error-message" role="alert">{{ unassignError }}</p>
				<div class="modal-actions">
					<button
						ref="unassignCancelButtonRef"
						class="modal-btn cancel"
						type="button"
						:disabled="unassignConfirmBusy"
						@click="closeUnassignConfirmation"
					>
						Cancel
					</button>
					<button
						class="modal-btn danger-confirm"
						type="button"
						:disabled="unassignConfirmBusy"
						@click="confirmUnassignSharedDocument"
					>
						{{ unassignConfirmBusy ? 'Removing...' : 'Remove assignment' }}
					</button>
				</div>
			</section>
		</div>

		<div v-if="showSigningProfileModal" class="modal-overlay" @click="closeSigningProfileModal">
			<div class="modal-content profile-modal" @click.stop>
				<h3 class="modal-title">My signing profile</h3>
				<p class="modal-message">
					This profile is used when you sign bilateral supplier documents before they are sent to the supplier. A single standard signature style is applied automatically.
				</p>

				<div class="form-group">
					<label for="signingLegalName">Legal name</label>
					<input
						id="signingLegalName"
						v-model="signingProfileForm.legalName"
						type="text"
						class="input"
						placeholder="Your legal signing name"
					/>
				</div>

				<div class="form-group">
					<label for="signingPhone">Phone (E.164)</label>
					<input
						id="signingPhone"
						v-model="signingProfileForm.phoneE164"
						type="text"
						class="input"
						placeholder="+447911123456"
					/>
					<p class="input-hint">OTP codes for owner signing will be sent to this number.</p>
				</div>

				<label class="check">
					<input v-model="signingConsentAccepted" type="checkbox" />
					<span>I agree to use this electronic signature for supplier documents.</span>
				</label>

				<p v-if="signingProfileError" class="error-message">{{ signingProfileError }}</p>

				<div class="modal-actions">
					<button class="modal-btn cancel" @click="closeSigningProfileModal">
						Cancel
					</button>
					<button
						class="modal-btn confirm"
						@click="saveSigningProfile"
						:disabled="adminStore.signingProfileLoading"
					>
						{{ adminStore.signingProfileLoading ? 'Saving...' : 'Save profile' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import {
	computed,
	getCurrentInstance,
	nextTick,
	onBeforeUnmount,
	onMounted,
	reactive,
	ref,
	watch,
} from 'vue';
import { BsChevronDown, BsFileEarmarkText } from '@kalimahapps/vue-icons';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import {
	normalizeSupplierDocumentCategory,
	useSupplierPortalAdminStore,
} from '@/stores/supplierPortalAdmin';
import { useSupplierPortalInsightsStore } from '@/stores/supplierPortalInsights';
import { createAttachmentFromFile, ensureAttachmentArray } from '@/utils/attachments';
import { useMediaStore } from '@/stores/media';
import { onEvent } from '@/utils/eventBus';
import {
	deriveAssignmentConfirmationDownloadFilename,
	getAssignmentConfirmationTableSummary,
	isAssignmentConfirmationDocument,
} from '@/utils/assignmentConfirmationDocument';

const DOCUMENT_GROUP_DEFINITIONS = [
	{
		value: 'assignments',
		label: 'Assignment confirmations',
		placeholder: 'Choose an assignment confirmation…',
		unavailableLabel: 'No confirmations available',
		emptyLabel: 'No assignment confirmations assigned.',
	},
	{
		value: 'statutory_policies',
		label: 'Policies',
		placeholder: 'Choose a policy…',
		unavailableLabel: 'No policies available',
		emptyLabel: 'No policies assigned.',
	},
	{
		value: 'health_and_safety',
		label: 'Health and safety',
		placeholder: 'Choose a health and safety document…',
		unavailableLabel: 'No safety documents available',
		emptyLabel: 'No health and safety documents assigned.',
	},
];

const props = defineProps({
	supplierId: {
		type: [String, Number],
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	attachments: {
		type: Array,
		default: () => [],
	},
	analysisEnabled: {
		type: Boolean,
		default: false,
	},
	analyzingAttachmentId: {
		type: [String, Number],
		default: null,
	},
});

const emit = defineEmits(['update:attachments', 'analyze-attachment']);

const adminStore = useSupplierPortalAdminStore();
const insightsStore = useSupplierPortalInsightsStore();
const mediaStore = useMediaStore();
const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;

const fileInput = ref(null);
const documentsExpanded = ref(true);
const attachmentsExpanded = ref(true);
const attachDocumentIds = reactive(
	Object.fromEntries(DOCUMENT_GROUP_DEFINITIONS.map(({ value }) => [value, ''])),
);
const expandedDocumentIds = reactive(
	Object.fromEntries(DOCUMENT_GROUP_DEFINITIONS.map(({ value }) => [value, ''])),
);
const openingId = ref(null);
const busyDocIds = ref([]);
const docsError = ref('');
const actionError = ref('');
const documentStatusMessage = ref('');
const unassignTarget = ref(null);
const unassignError = ref('');
const unassignDialogRef = ref(null);
const unassignCancelButtonRef = ref(null);
const ownerEffectiveDates = ref({});
const ownerOtpCodes = ref({});
const ownerOtpPendingByDocumentId = ref({});
const showSigningProfileModal = ref(false);
const signingConsentAccepted = ref(false);
const signingProfileError = ref('');
const signingProfileForm = reactive({
	legalName: '',
	phoneE164: '',
});
const documentToggleRefs = new Map();
const assignSelectRefs = new Map();
let unassignTriggerElement = null;
let unassignBodyOverflow = null;

let catalogUnsubscribe = null;
let documentsUnsubscribe = null;

const supplierKey = computed(() => String(props.supplierId || '').trim());
const attachmentsModel = computed({
	get: () => ensureAttachmentArray(props.attachments),
	set: (value) => emit('update:attachments', ensureAttachmentArray(value)),
});
const portalEntry = computed(() => {
	if (!supplierKey.value) return null;
	return (
		insightsStore.suppliers.find(
			(entry) => String(entry?.supplier?.id || '') === supplierKey.value,
		) || null
	);
});
const supplierDocuments = computed(() => {
	const docs = portalEntry.value?.documents;
	return Array.isArray(docs) ? docs : [];
});
const visibleAttachments = computed(() =>
	attachmentsModel.value.filter((attachment) => attachment.status !== 'delete'),
);
const attachmentKey = (attachment) => attachment?.id || attachment?.localId || null;
const isPdfAttachment = (attachment) => {
	const mime = String(attachment?.mime || attachment?.file?.type || '').toLowerCase();
	const name = String(attachment?.name || attachment?.file?.name || '').toLowerCase();
	return mime === 'application/pdf' || name.endsWith('.pdf');
};
const hasRightToWorkPrefix = (attachment) => {
	const name = String(attachment?.name || attachment?.file?.name || '').trim().toUpperCase();
	return /^(?:RW|RTW)[\s_-]/.test(name);
};
const isRightToWorkAttachment = (attachment) => (
	hasRightToWorkPrefix(attachment) && isPdfAttachment(attachment)
);
const canAnalyzeAttachment = (attachment) => Boolean(
	attachment?.id && isRightToWorkAttachment(attachment),
);
const isAnalyzingAttachment = (attachment) => (
	props.analyzingAttachmentId !== null
	&& String(props.analyzingAttachmentId) === String(attachmentKey(attachment))
);
const analysisButtonTitle = (attachment) => {
	if (!attachment?.id) return 'Wait for the attachment to finish saving';
	if (!isPdfAttachment(attachment)) {
		return 'Right to Work AI currently supports PDF files only';
	}
	return 'Extract Right to Work details from this PDF';
};
const docsLoading = computed(
	() =>
		(adminStore.loading || insightsStore.loading) &&
		!(adminStore.documents || []).length &&
		!supplierDocuments.value.length,
);
const isBusy = computed(() => insightsStore.loading || adminStore.loading);
const adminDocumentsById = computed(
	() => new Map((adminStore.documents || []).map((doc) => [String(doc?.id || ''), doc])),
);
const getDocumentCategory = (doc) => {
	const ownCategory = String(doc?.category || '').trim();
	const adminCategory = adminDocumentsById.value.get(String(doc?.id || ''))?.category;
	return normalizeSupplierDocumentCategory(ownCategory || adminCategory);
};
const getDocumentReference = (doc) =>
	getAssignmentConfirmationTableSummary(doc)?.reference ||
	getAssignmentConfirmationTableSummary(adminDocumentsById.value.get(String(doc?.id || '')))
		?.reference ||
	'';
const getDocumentLabel = (doc) => {
	const title = doc?.title || 'Untitled document';
	const reference = getDocumentReference(doc);
	return reference && reference !== title ? `${title} ${reference}` : title;
};
const getAssignmentDownloadFilename = (doc, signed = false) => {
	const source = adminDocumentsById.value.get(String(doc?.id || '')) || doc;
	if (!isAssignmentConfirmationDocument(source) && !isAssignmentConfirmationDocument(doc)) {
		return '';
	}
	return deriveAssignmentConfirmationDownloadFilename(
		{ ...source, reference: getDocumentReference(doc) },
		{ supplierName: portalEntry.value?.supplier?.name, signed },
	);
};
const getSelectableDocumentTitle = (doc) => {
	if (getDocumentCategory(doc) === 'assignments') {
		const summary = getAssignmentConfirmationTableSummary(doc);
		const distinguishingParts = [summary?.reference, summary?.counterparty].filter(Boolean);
		if (distinguishingParts.length) return distinguishingParts.join(' — ');
	}
	return doc?.title || 'Untitled document';
};
const normalizeSupplierId = (value) => String(value ?? '').trim();
const isDocumentForCurrentSupplier = (doc) => {
	if (!isAssignmentConfirmationDocument(doc)) return true;

	const linkedSupplierIds = (Array.isArray(doc?.supplierIds) ? doc.supplierIds : [])
		.map(normalizeSupplierId)
		.filter(Boolean);
	if (linkedSupplierIds.length) {
		return linkedSupplierIds.includes(supplierKey.value);
	}

	return normalizeSupplierId(doc?.content?.subcontractorId) === supplierKey.value;
};
const selectableDocuments = computed(() => {
	const visibleIds = new Set(
		supplierDocuments.value.map((doc) => String(doc?.id || '')).filter(Boolean),
	);

	return (adminStore.documents || [])
		.filter((doc) => Boolean(doc?.file?.id))
		.filter((doc) => !doc?.isArchived)
		.filter(isDocumentForCurrentSupplier)
		.filter((doc) => !visibleIds.has(String(doc.id)))
		.map((doc) => ({
			id: doc.id,
			title: getSelectableDocumentTitle(doc),
			category: getDocumentCategory(doc),
		}));
});
const documentGroups = computed(() =>
	DOCUMENT_GROUP_DEFINITIONS.map((definition) => {
		const documents = supplierDocuments.value.filter(
			(doc) => getDocumentCategory(doc) === definition.value,
		);
		const availableDocuments = selectableDocuments.value.filter(
			(doc) => doc.category === definition.value,
		);
		return {
			...definition,
			documents,
			selectableDocuments: availableDocuments,
			selectOptions: [
				{ id: '', name: availableDocuments.length ? definition.placeholder : definition.unavailableLabel },
				...availableDocuments.map((doc) => ({ id: String(doc.id), name: doc.title })),
			],
		};
	}),
);

watch(selectableDocuments, (documents) => {
	DOCUMENT_GROUP_DEFINITIONS.forEach(({ value }) => {
		const selectedId = String(attachDocumentIds[value] || '');
		if (!selectedId) return;
		const remainsAvailable = documents.some(
			(doc) => doc.category === value && String(doc.id) === selectedId,
		);
		if (!remainsAvailable) attachDocumentIds[value] = '';
	});
});

const needsSigningProfile = computed(
	() =>
		supplierDocuments.value.some((doc) => doc?.completionMode === 'bilateral_signature') &&
		!adminStore.signingProfile?.isComplete,
);

const formatDateTime = (value, options = {}) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	if (options?.dateOnly) {
		return date.toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
		});
	}
	return date.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const formatDocumentEvent = (value, options = {}) => {
	const formatted = value ? formatDateTime(value, options) : '';
	return formatted || 'Not yet';
};

const documentDetailsId = (doc) =>
	`supplier-doc-details-${String(doc?.id || 'document').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const isDocumentExpanded = (doc) => {
	const category = getDocumentCategory(doc);
	return expandedDocumentIds[category] === String(doc?.id || '');
};

const toggleDocument = (doc) => {
	const category = getDocumentCategory(doc);
	const documentId = String(doc?.id || '');
	if (!documentId) return;
	expandedDocumentIds[category] =
		expandedDocumentIds[category] === documentId ? '' : documentId;
};

const setDocumentToggleRef = (doc, element) => {
	const documentId = String(doc?.id || '');
	if (!documentId) return;
	if (element) documentToggleRefs.set(documentId, element);
	else documentToggleRefs.delete(documentId);
};

const setAssignSelectRef = (category, element) => {
	const normalizedCategory = normalizeSupplierDocumentCategory(category);
	if (element) assignSelectRefs.set(normalizedCategory, element);
	else assignSelectRefs.delete(normalizedCategory);
};

const resetDocumentUiState = () => {
	DOCUMENT_GROUP_DEFINITIONS.forEach(({ value }) => {
		attachDocumentIds[value] = '';
		expandedDocumentIds[value] = '';
	});
	documentStatusMessage.value = '';
};

const hasMobileAccess = () => {
	const portal = portalEntry.value?.portal || {};
	if (!portal?.hasProfile) return false;
	if (portal.enabled === false) return false;
	if (portal.mobileEnabled === false) return false;
	if (!portal.phoneE164) return false;
	return true;
};

const getDocState = (doc) => {
	if (!doc?.id) return 'unknown';
	if (doc?.completionMode === 'bilateral_signature') {
		const executionStatus = String(doc?.executionStatus || '');
		if (executionStatus === 'completed') return 'completed';
		if (executionStatus === 'awaiting_supplier_signature') return 'awaiting_supplier';
		if (executionStatus === 'superseded') return 'superseded';
		if (executionStatus === 'cancelled') return 'cancelled';
		return 'awaiting_owner';
	}

	if (!hasMobileAccess()) return 'no_access';
	return 'not_required';
};

const docStateLabel = (state) => {
	if (state === 'completed') return 'Completed';
	if (state === 'awaiting_owner') return 'Awaiting owner signature';
	if (state === 'awaiting_supplier') return 'Awaiting supplier signature';
	if (state === 'superseded') return 'Superseded';
	if (state === 'cancelled') return 'Cancelled';
	if (state === 'not_required') return 'Read only';
	if (state === 'no_access') return 'No access';
	return 'Unknown';
};

const docBadgeClass = (state) => {
	if (state === 'completed') return 'badge-success';
	if (state === 'awaiting_owner' || state === 'awaiting_supplier') {
		return 'badge-warning';
	}
	if (state === 'superseded' || state === 'cancelled') {
		return 'badge-danger';
	}
	return 'badge-muted';
};

const isDocBusy = (doc) => busyDocIds.value.includes(String(doc?.id || ''));

const setDocBusy = (docId, state) => {
	const id = String(docId || '');
	const current = new Set(busyDocIds.value);
	if (state) current.add(id);
	else current.delete(id);
	busyDocIds.value = Array.from(current);
};

const unassignConfirmBusy = computed(() =>
	unassignTarget.value ? isDocBusy(unassignTarget.value) : false,
);

const lockUnassignModalScroll = () => {
	if (typeof document === 'undefined' || unassignBodyOverflow !== null) return;
	unassignBodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
};

const unlockUnassignModalScroll = () => {
	if (typeof document === 'undefined' || unassignBodyOverflow === null) return;
	document.body.style.overflow = unassignBodyOverflow;
	unassignBodyOverflow = null;
};

const requestUnassignSharedDocument = (doc, event) => {
	if (props.disabled || !supplierKey.value || !doc?.id) return;
	unassignTriggerElement = event?.currentTarget || null;
	unassignTarget.value = doc;
	unassignError.value = '';
	lockUnassignModalScroll();
	nextTick(() => unassignCancelButtonRef.value?.focus?.());
};

const closeUnassignConfirmation = ({ restoreFocus = true } = {}) => {
	if (unassignConfirmBusy.value) return;
	unassignTarget.value = null;
	unassignError.value = '';
	unlockUnassignModalScroll();
	if (restoreFocus) {
		const trigger = unassignTriggerElement;
		nextTick(() => trigger?.isConnected && trigger.focus?.());
	}
	unassignTriggerElement = null;
};

const trapUnassignModalFocus = (event) => {
	const focusable = Array.from(
		unassignDialogRef.value?.querySelectorAll('button:not(:disabled), [tabindex]:not([tabindex="-1"])') || [],
	);
	if (!focusable.length) {
		event.preventDefault();
		unassignDialogRef.value?.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

const syncSigningProfileForm = (profile = adminStore.signingProfile) => {
	signingProfileForm.legalName = profile?.legalName || '';
	signingProfileForm.phoneE164 = profile?.phoneE164 || '';
	signingConsentAccepted.value = Boolean(profile?.eSignConsentAt);
	signingProfileError.value = '';
};

const openFilePicker = () => {
	if (props.disabled) return;
	attachmentsExpanded.value = true;
	fileInput.value?.click?.();
};

const onFileChange = (event) => {
	if (props.disabled) return;
	const files = Array.from(event?.target?.files || []);
	addUploadedFiles(files);
	if (event?.target) {
		event.target.value = '';
	}
};

const addUploadedFiles = (files) => {
	if (!files?.length) return;
	const current = attachmentsModel.value.slice();
	files.forEach((file) => {
		if (!file) return;
		const duplicate = current.some(
			(attachment) =>
				attachment.name === file.name &&
				attachment.size === file.size &&
				attachment.status !== 'delete',
		);
		if (duplicate) {
			toast?.info?.(`${file.name} is already attached.`);
			return;
		}
		current.push(createAttachmentFromFile(file));
	});
	attachmentsModel.value = current;
};

const removeUploadedAttachment = (attachment) => {
	if (!attachment) return;
	const current = attachmentsModel.value.slice();
	const index = current.findIndex(
		(item) => item.localId === attachment.localId || item.id === attachment.id,
	);
	if (index === -1) return;
	if (!current[index]?.id) {
		current.splice(index, 1);
	} else {
		current[index] = { ...current[index], status: 'delete' };
	}
	attachmentsModel.value = current;
};

const openUploadedAttachment = async (attachment, key) => {
	if (!attachment) return;
	openingId.value = key;
	let url = '';
	let revokeBlobUrl = false;
	try {
		if (attachment.file) {
			url = URL.createObjectURL(attachment.file);
			revokeBlobUrl = true;
		} else if (attachment.id) {
			const signed = await mediaStore.getSignedUrl(attachment.id);
			url = signed?.url || '';
		}
		if (!url) {
			toast?.warn?.('No preview available for this file.');
			return;
		}
		window.open(url, '_blank', 'noopener');
	} catch (error) {
		toast?.error?.('Unable to open attachment.');
		logError?.(error, 'SupplierAccessPanel::openUploadedAttachment');
	} finally {
		if (revokeBlobUrl && url) {
			setTimeout(() => {
				URL.revokeObjectURL(url);
			}, 1000);
		}
		openingId.value = null;
	}
};

const openOrDownloadDocument = async (url, filename) => {
	if (!filename) {
		window.open(url, '_blank', 'noopener');
		return;
	}
	// A cross-origin link ignores `download`; use the original PDF bytes in a local blob.
	const response = await fetch(url);
	if (!response.ok) throw new Error(`Document download failed (${response.status})`);
	const objectUrl = URL.createObjectURL(await response.blob());
	const link = document.createElement('a');
	try {
		link.href = objectUrl;
		link.download = filename;
		link.style.display = 'none';
		document.body.appendChild(link);
		link.click();
	} finally {
		link.remove();
		setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
	}
};

const openSharedDocument = async (doc, key) => {
	const filename = getAssignmentDownloadFilename(doc);
	openingId.value = key;
	try {
		const mediaId = doc?.file?.id;
		if (!mediaId) {
			toast?.warn?.('No preview available for this file.');
			return;
		}
		const signed = await mediaStore.getSignedUrl(mediaId);
		const url = signed?.url || '';
		if (!url) {
			toast?.warn?.('No preview available for this file.');
			return;
		}
		await openOrDownloadDocument(url, filename);
	} catch (error) {
		toast?.error?.(filename ? 'Unable to download supplier document.' : 'Unable to open supplier document.');
		logError?.(error, 'SupplierAccessPanel::openSharedDocument');
	} finally {
		openingId.value = null;
	}
};

const refreshOverview = async () => {
	if (!supplierKey.value) return;
	try {
		await insightsStore.fetchOverview();
	} catch (error) {
		logError?.(error, 'SupplierAccessPanel::refreshOverview');
	}
};

const refreshDocuments = async () => {
	try {
		docsError.value = '';
		await adminStore.fetchDocuments();
	} catch (error) {
		docsError.value = adminStore.error || 'Failed to load supplier docs';
	}
};

const refreshSigningProfile = async () => {
	try {
		await adminStore.fetchSigningProfile();
	} catch (error) {
		// keep silent until user explicitly opens the modal
	}
};

const attachSelected = async (category) => {
	const normalizedCategory = normalizeSupplierDocumentCategory(category);
	const documentId = String(attachDocumentIds[normalizedCategory] || '').trim();
	if (!supplierKey.value || !documentId) return;
	const group = documentGroups.value.find(({ value }) => value === normalizedCategory);
	const selectedDocument = group?.selectableDocuments.find(
		(doc) => String(doc.id) === documentId,
	);
	if (!selectedDocument) {
		attachDocumentIds[normalizedCategory] = '';
		actionError.value = 'That document is no longer available in this category.';
		await nextTick();
		assignSelectRefs.get(normalizedCategory)?.focusInput?.();
		return;
	}
	actionError.value = '';
	docsError.value = '';
	documentStatusMessage.value = '';
	try {
		await adminStore.attachDocumentToSupplier({
			supplierId: supplierKey.value,
			documentId,
		});
		attachDocumentIds[normalizedCategory] = '';
		await Promise.allSettled([refreshDocuments(), refreshOverview()]);
		expandedDocumentIds[normalizedCategory] = documentId;
		documentStatusMessage.value = `${selectedDocument.title} assigned.`;
		await nextTick();
		documentToggleRefs.get(documentId)?.focus?.();
		toast?.success?.('Document added');
	} catch (error) {
		actionError.value = adminStore.error || 'Failed to add document';
		logError?.(error, 'SupplierAccessPanel::attachSelected');
	}
};

const unassignSharedDocument = async (doc) => {
	if (!supplierKey.value || !doc?.id) return false;
	const category = getDocumentCategory(doc);
	const documentTitle = getDocumentLabel(doc);
	setDocBusy(doc.id, true);
	actionError.value = '';
	unassignError.value = '';
	documentStatusMessage.value = '';
	try {
		await adminStore.detachDocumentFromSupplier({
			supplierId: supplierKey.value,
			documentId: doc.id,
		});
		await Promise.allSettled([refreshDocuments(), refreshOverview()]);
		expandedDocumentIds[category] = '';
		documentStatusMessage.value = `${documentTitle} removed.`;
		await nextTick();
		assignSelectRefs.get(category)?.focusInput?.();
		toast?.success?.('Document removed');
		return true;
	} catch (error) {
		const message = adminStore.error || 'Failed to unassign supplier document';
		actionError.value = message;
		unassignError.value = message;
		logError?.(error, 'SupplierAccessPanel::unassignSharedDocument');
		return false;
	} finally {
		setDocBusy(doc.id, false);
	}
};

const confirmUnassignSharedDocument = async () => {
	const doc = unassignTarget.value;
	if (!doc || unassignConfirmBusy.value) return;
	const removed = await unassignSharedDocument(doc);
	if (removed) closeUnassignConfirmation({ restoreFocus: false });
};

const getEffectiveDateValue = (doc) =>
	ownerEffectiveDates.value[String(doc?.id || '')] || doc?.effectiveDate || '';

const setEffectiveDateValue = (doc, value) => {
	const key = String(doc?.id || '');
	ownerEffectiveDates.value = {
		...ownerEffectiveDates.value,
		[key]: String(value || ''),
	};
};

const getOwnerOtpCode = (doc) => ownerOtpCodes.value[String(doc?.id || '')] || '';

const setOwnerOtpCode = (doc, value) => {
	const key = String(doc?.id || '');
	ownerOtpCodes.value = {
		...ownerOtpCodes.value,
		[key]: String(value || '').trim(),
	};
};

const hasOwnerOtpPending = (doc) => Boolean(ownerOtpPendingByDocumentId.value[String(doc?.id || '')]);

const canPrepareOwnerSignature = (doc) =>
	doc?.completionMode === 'bilateral_signature' &&
	(doc?.executionStatus === 'awaiting_owner_signature' ||
		doc?.executionStatus === 'draft' ||
		doc?.executionStatus === 'superseded' ||
		doc?.executionStatus === 'cancelled' ||
		!doc?.executionStatus);

const hasSigningSection = (doc) => {
	if (doc?.completionMode !== 'bilateral_signature') return false;
	if (canPrepareOwnerSignature(doc)) return true;
	if (hasOwnerOtpPending(doc)) return true;
	return false;
};

const openSigningProfileModal = async () => {
	signingProfileError.value = '';
	if (!adminStore.signingProfile && !adminStore.signingProfileLoading) {
		try {
			await adminStore.fetchSigningProfile();
		} catch (error) {
			signingProfileError.value = adminStore.error || 'Failed to load signing profile';
		}
	}
	syncSigningProfileForm();
	showSigningProfileModal.value = true;
};

const closeSigningProfileModal = () => {
	showSigningProfileModal.value = false;
	signingProfileError.value = '';
};

const saveSigningProfile = async () => {
	signingProfileError.value = '';
	try {
		await adminStore.saveSigningProfile({
			legalName: signingProfileForm.legalName,
			phoneE164: signingProfileForm.phoneE164,
			consentAccepted: signingConsentAccepted.value,
		});
		syncSigningProfileForm();
		showSigningProfileModal.value = false;
		toast?.success?.('Signing profile updated');
	} catch (error) {
		signingProfileError.value = adminStore.error || 'Failed to save signing profile';
		logError?.(error, 'SupplierAccessPanel::saveSigningProfile');
	}
};

const requestOwnerOtp = async (doc) => {
	if (!supplierKey.value || !doc?.id) return;
	if (needsSigningProfile.value) {
		toast?.info?.('Complete your signing profile before you send owner OTP codes.');
		await openSigningProfileModal();
		return;
	}

	setDocBusy(doc.id, true);
	actionError.value = '';
	try {
		await adminStore.requestOwnerSignOtp({
			supplierId: supplierKey.value,
			documentId: doc.id,
			effectiveDate:
				doc?.completionMode === 'bilateral_signature'
					? getEffectiveDateValue(doc)
					: null,
		});
		ownerOtpPendingByDocumentId.value = {
			...ownerOtpPendingByDocumentId.value,
			[String(doc.id)]: true,
		};
		toast?.success?.('OTP sent to your signing phone');
		await refreshOverview();
	} catch (error) {
		actionError.value = adminStore.error || 'Failed to request owner OTP';
		logError?.(error, 'SupplierAccessPanel::requestOwnerOtp');
	} finally {
		setDocBusy(doc.id, false);
	}
};

const confirmOwnerSignature = async (doc) => {
	if (!doc?.executionId) return;
	setDocBusy(doc.id, true);
	actionError.value = '';
	try {
		await adminStore.signExecution({
			executionId: doc.executionId,
			code: getOwnerOtpCode(doc),
		});
		setOwnerOtpCode(doc, '');
		ownerOtpPendingByDocumentId.value = {
			...ownerOtpPendingByDocumentId.value,
			[String(doc.id)]: false,
		};
		toast?.success?.('Document signed as owner');
		await refreshOverview();
	} catch (error) {
		actionError.value = adminStore.error || 'Failed to sign document';
		logError?.(error, 'SupplierAccessPanel::confirmOwnerSignature');
	} finally {
		setDocBusy(doc.id, false);
	}
};

const downloadExecutedCopy = async (doc) => {
	if (!doc?.executionId) return;
	const filename = getAssignmentDownloadFilename(doc, true);
	setDocBusy(doc.id, true);
	actionError.value = '';
	try {
		const response = await adminStore.getExecutionDownloadUrl(doc.executionId);
		const url = response?.url || '';
		if (!url) {
			toast?.error?.('No executed copy is available yet');
			return;
		}
		await openOrDownloadDocument(url, filename);
	} catch (error) {
		actionError.value = adminStore.error || 'Failed to load executed copy';
		logError?.(error, 'SupplierAccessPanel::downloadExecutedCopy');
	} finally {
		setDocBusy(doc.id, false);
	}
};

const handleCatalogChange = (detail = {}) => {
	const catalogName = String(detail?.catalogName || '').trim().toLowerCase();
	const ids = Array.isArray(detail?.ids) ? detail.ids.map((id) => String(id)) : [];
	if (catalogName !== 'suppliers' && !ids.includes(String(supplierKey.value))) return;
	void refreshOverview();
};

const handleDocumentsChange = () => {
	void Promise.allSettled([refreshDocuments(), refreshOverview(), refreshSigningProfile()]);
};

watch(
	() => supplierKey.value,
	(next) => {
		resetDocumentUiState();
		actionError.value = '';
		docsError.value = '';
		ownerEffectiveDates.value = {};
		ownerOtpCodes.value = {};
		ownerOtpPendingByDocumentId.value = {};
		if (!next) return;
		void Promise.allSettled([refreshOverview(), refreshDocuments(), refreshSigningProfile()]);
	},
	{ immediate: true },
);

onMounted(() => {
	catalogUnsubscribe = onEvent('catalog:changed', handleCatalogChange);
	documentsUnsubscribe = onEvent('portal-documents:changed', handleDocumentsChange);
});

onBeforeUnmount(() => {
	unlockUnassignModalScroll();
	if (catalogUnsubscribe) {
		catalogUnsubscribe();
		catalogUnsubscribe = null;
	}
	if (documentsUnsubscribe) {
		documentsUnsubscribe();
		documentsUnsubscribe = null;
	}
});
</script>

<style scoped>
.supplier-access-panel {
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 16px;
}

/* --- Panel card -------------------------------------------------------- */
.panel-card {
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 8px;
	box-shadow: var(--box-shadow);
	overflow: hidden;
}

.panel-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 12px 18px;
	background: var(--noc-background);
}

.panel-card__title {
	margin: 0;
	font-size: 0.95rem;
	font-weight: 600;
	color: var(--text-color);
	letter-spacing: 0.01em;
	min-width: 0;
}

.panel-card__body {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 16px 18px;
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

.panel-card__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	padding: 12px 20px;
	border-top: 1px solid var(--border-color);
	background: var(--noc-background-secondary, #fafafa);
}

/* --- Categorised supplier documents ----------------------------------- */
.supplier-section {
	border: 0;
	border-radius: 0;
	box-shadow: none;
	background: transparent;
	overflow: visible;
}

.supplier-section > .panel-card__header {
	padding: 0;
	background: transparent;
}

.supplier-section .panel-card__title {
	width: 100%;
}

.supplier-section > .panel-card__body {
	padding: 0 0 0 20px;
}

.documents-toggle {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
	width: fit-content;
	max-width: 100%;
	min-height: 44px;
	padding: 0 0 12px;
	border: 0;
	background: transparent;
	color: var(--text-color);
	font: 600 16px/20px var(--font-family-base);
	text-align: left;
	cursor: pointer;
}

.documents-toggle__label {
	position: relative;
}

.documents-toggle__label::after {
	content: '';
	position: absolute;
	left: 0;
	bottom: -7px;
	width: 20px;
	height: 2px;
	border-radius: 2px;
	background: var(--noc-blue, #0065ff);
	pointer-events: none;
	transform: scaleX(0);
	transform-origin: left center;
	transition: transform 200ms ease;
}

.documents-toggle[aria-expanded='true'] .documents-toggle__label::after {
	transform: scaleX(1);
}

.documents-toggle__chevron {
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	stroke: currentColor;
	stroke-width: 0.75px;
	stroke-linejoin: round;
	transform: rotate(-90deg);
	transition: transform 150ms ease;
}

.documents-toggle__chevron.open {
	transform: rotate(0deg);
}

.supplier-section :is(button, select) {
	touch-action: manipulation;
}

.document-groups {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.document-group {
	min-width: 0;
}

.document-group__header {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 10px;
	padding: 0;
}

.document-group__identity {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-width: 0;
	flex: 0 0 auto;
}

.document-group__title {
	margin: 0;
	font: 600 14px/20px var(--font-family-base);
	color: var(--text-color);
}

.document-group__empty {
	margin: 0;
	padding: 10px 12px;
	border-bottom: 1px solid var(--border-color);
	border-inline: 1px solid var(--border-color);
	min-height: 44px;
	box-sizing: border-box;
	background: var(--noc-background);
	color: var(--text-color-secondary);
	font: 400 14px/20px var(--font-family-base);
}

/* --- Assign row -------------------------------------------------------- */
.assign-row {
	padding-right: 8px;
	border: 1px solid var(--border-color);
	box-sizing: border-box;
	background: var(--noc-background);
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 0 0 auto;
	justify-content: flex-end;
	width: 100%;
	min-width: 0;
}

.assign-row__select {
	--catalog-input-border: 0;
	--catalog-input-height: 44px;
	--catalog-input-border-radius: 0;
	--catalog-input-padding: 0 12px 0 36px;
	--catalog-input-font-size: 14px;
	--catalog-input-font-family: var(--font-family-base);
	--catalog-input-background: var(--noc-background);
	--catalog-input-focus-border-color: var(--border-color);
	position: relative;
	flex: 1 1 0;
	min-width: 0;
}

.assign-row__select :deep(.field-input) {
	line-height: 20px;
	box-shadow: none;
}

.assign-row__select :deep(.field-input:hover),
.assign-row__select :deep(.field-input:focus) {
	border-color: var(--border-color);
}

.assign-row__select :deep(.field-input:hover:not(:disabled):not(:focus)) {
	background: var(--noc-background);
}

.assign-row__select :deep(.field-input:disabled) {
	background: var(--noc-background);
	color: var(--text-color-secondary);
	cursor: not-allowed;
	opacity: 1;
}

.assign-row__select :deep(.dropdown-menu) {
	margin-top: 0;
}

.assign-row__select :deep(.dropdown-menu li) {
	min-height: 40px;
	padding: 10px 12px;
	box-sizing: border-box;
	font: 400 14px/20px var(--font-family-base);
	overflow-wrap: anywhere;
}

.assign-row__select :deep(.dropdown-menu li.is-selected) {
	font-weight: 600;
}

.assign-row__plus {
	position: absolute;
	left: 12px;
	z-index: 1;
	top: 50%;
	transform: translateY(-50%);
	color: var(--text-color-secondary);
	pointer-events: none;
}

.document-action-btn {
	--assign-button-height: 32px;
	--assign-button-background: var(--noc-background);
	--assign-button-border: var(--border-color);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	height: var(--assign-button-height);
	min-height: var(--assign-button-height);
	padding: 0 10px;
	flex-shrink: 0;
	box-sizing: border-box;
	border: 1px solid var(--assign-button-border);
	border-radius: 6px;
	background: var(--assign-button-background);
	color: var(--text-color);
	font: 600 14px/20px var(--font-family-base);
	box-shadow: none;
	transform: none;
	filter: none;
	appearance: none;
	cursor: pointer;
	touch-action: manipulation;
	transition: background-color 120ms ease, border-color 120ms ease;
}

.document-action-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.assign-row--selected .assign-row__btn {
	--assign-button-background: var(--noc-blue, #2563eb);
	--assign-button-border: var(--noc-blue, #2563eb);
	color: #fff;
}

@media (hover: hover) and (pointer: fine) {
	.document-action-btn:hover:not(:disabled) {
		--assign-button-background: var(--noc-background-secondary, #fafafb);
	}

	.assign-row--selected .assign-row__btn:hover:not(:disabled) {
		--assign-button-background: color-mix(in srgb, var(--noc-blue, #2563eb) 94%, #000);
		--assign-button-border: var(--assign-button-background);
	}
}

.assign-row--selected .assign-row__plus {
	color: var(--noc-blue, #2563eb);
}

/* --- Notice card (signing profile required) ---------------------------- */
.notice {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 12px 16px;
	border: 1px solid rgba(217, 119, 6, 0.25);
	background: rgba(245, 158, 11, 0.08);
	border-radius: 6px;
}

.notice__body {
	flex: 1;
	min-width: 0;
}

.notice__title {
	margin: 0 0 2px;
	font-size: 0.92rem;
	font-weight: 600;
	color: #92400e;
}

.notice__hint {
	margin: 0;
	font-size: 0.85rem;
	color: var(--text-color-secondary);
	line-height: 1.4;
}

/* --- Compact document disclosure list --------------------------------- */
.doc-list,
.file-list {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
}

.file-list {
	border: 1px solid var(--border-color);
	border-radius: 0;
}

.doc-list {
	border-inline: 1px solid var(--border-color);
}

.doc-item {
	background: var(--noc-background);
	border-bottom: 1px solid var(--border-color);
}

.doc-item:not(:last-child) {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.doc-item__toggle {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
	width: 100%;
	min-height: 44px;
	padding: 10px 12px;
	border: 0;
	background: transparent;
	color: var(--text-color);
	font-family: inherit;
	text-align: left;
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.doc-item__toggle:hover {
	background: var(--noc-background-secondary, #fafafb);
}

.doc-item__toggle:focus-visible {
	outline: none;
	background: var(--noc-background-secondary, #fafafb);
}

.doc-item--expanded .doc-item__toggle {
	background: var(--noc-background-secondary, #fafafb);
}

.doc-item__name {
	min-width: 0;
	font: 600 14px/20px var(--font-family-base);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.doc-item__name--with-reference {
	white-space: normal;
	overflow-wrap: anywhere;
}

.doc-item__chevron {
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	color: var(--text-color);
	stroke: currentColor;
	stroke-width: 0.75px;
	stroke-linejoin: round;
	transform: rotate(-90deg);
	transition: transform 0.15s ease;
}

.doc-item__chevron.open {
	transform: rotate(0deg);
}

.doc-item__details {
	padding: 2px 12px 12px;
	background: var(--noc-background);
}

.doc-item__summary {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 4px 0 12px;
}

/* Document activity is a compact, wrapping description list, without cells. */
.doc-item__activity {
	display: flex;
	flex-wrap: wrap;
	gap: 12px 24px;
	margin: 0;
	padding: 0;
}

.doc-item__meta-entry {
	flex: 0 1 160px;
	min-width: 0;
}

.doc-item__meta-entry dt {
	margin: 0 0 2px;
	color: var(--text-color-secondary);
	font: 400 12px/18px var(--font-family-base);
}

.doc-item__meta-entry dd {
	margin: 0;
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base);
	font-variant-numeric: tabular-nums;
	overflow-wrap: anywhere;
}

.doc-item__meta-entry dd.is-pending {
	color: var(--text-color-secondary);
}

.doc-item__signing {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
	margin-top: 14px;
}

.signing-controls {
	--signing-control-height: 36px;
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 8px;
}

.signing-field {
	display: flex;
	flex-direction: column;
	gap: 5px;
	min-width: 0;
}

.signing-field__label {
	font-size: 0.72rem;
	font-weight: 600;
	color: var(--text-color-secondary);
}

.doc-item__actions {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 4px 20px;
	flex-wrap: wrap;
	margin-top: 12px;
}

.doc-item__actions .link-btn {
	min-height: 32px;
	font: 500 13px/20px var(--font-family-base);
}

.doc-item__actions .link-btn--danger {
	margin-inline-start: auto;
}

.doc-details-enter-active,
.doc-details-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.doc-details-enter-from,
.doc-details-leave-to {
	opacity: 0;
	transform: translateY(-3px);
}

.file-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	min-height: 44px;
	padding: 6px 12px;
	background: var(--noc-background);
	border-bottom: 1px solid var(--border-color);
	box-sizing: border-box;
}

.file-item:last-child {
	border-bottom: none;
}
.file-item__actions {
	display: flex;
	align-items: center;
	gap: 4px 20px;
	flex-wrap: wrap;
	flex-shrink: 0;
}

.file-item__actions .link-btn {
	min-height: 32px;
	font: 500 13px/20px var(--font-family-base);
}

.supplier-attachments-section > .panel-card__header {
	align-items: flex-start;
}

.attachments-empty {
	margin: 0;
	min-height: 44px;
	padding: 10px 12px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	color: var(--text-color-secondary);
	font: 400 14px/20px var(--font-family-base);
}

.link-btn--ai {
	color: var(--noc-blue, #0a61c9);
	font-weight: 700;
}

/* --- File list --------------------------------------------------------- */
.file-item__main {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
	flex: 1;
}

.file-item__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	color: var(--text-color-secondary);
	flex-shrink: 0;
	font-size: 16px;
}

.file-item__name {
	font: 600 14px/20px var(--font-family-base);
	color: var(--text-color);
	overflow-wrap: anywhere;
}

.file-input {
	display: none;
}

/* --- Buttons ----------------------------------------------------------- */
.link-btn {
	border: 0;
	background: none;
	padding: 0;
	font-size: 0.88rem;
	font-weight: 600;
	color: var(--noc-blue);
	cursor: pointer;
}

.link-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.link-btn:hover:not(:disabled) {
	text-decoration: underline;
}

.document-action-btn:focus-visible,
.link-btn:focus-visible {
	outline: 2px solid var(--noc-blue, #2563eb);
	outline-offset: 2px;
}

.link-btn--danger {
	color: var(--noc-red, #d9534f);
}

.btn-icon {
	font-size: 0.95rem;
	margin-right: 2px;
}

/* --- Badges ------------------------------------------------------------ */
.badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 999px;
	padding: 3px 10px;
	font-size: 0.75rem;
	font-weight: 600;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	color: var(--text-color-secondary);
	white-space: nowrap;
}

.badge-success {
	border-color: rgba(22, 163, 74, 0.2);
	background: rgba(22, 163, 74, 0.08);
	color: #15803d;
}

.badge-warning {
	border-color: rgba(217, 119, 6, 0.2);
	background: rgba(245, 158, 11, 0.1);
	color: #b45309;
}

.badge-danger {
	border-color: rgba(220, 38, 38, 0.2);
	background: rgba(220, 38, 38, 0.08);
	color: #b91c1c;
}

.badge-muted {
	background: rgba(148, 163, 184, 0.12);
	color: #64748b;
}

/* --- Misc hints & errors ---------------------------------------------- */
.loading-hint,
.empty-hint {
	margin: 0;
	padding: 2px 0;
	font-size: 0.84rem;
	color: var(--text-color-secondary);
}

.inline-error {
	margin: 0;
	font-size: 0.85rem;
	color: var(--noc-red, #d9534f);
}

.error-message {
	margin: 0;
	color: var(--noc-red, #d9534f);
}

/* --- Modals ------------------------------------------------------------ */
.modal-overlay {
	position: fixed;
	inset: 0;
	background: rgba(15, 23, 42, 0.35);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
	z-index: 2000;
}

.modal-content {
	width: min(100%, 480px);
	background: var(--noc-background);
	border-radius: 12px;
	box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
	padding: 22px;
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.unassign-confirm-modal {
	width: min(100%, 420px);
}

.modal-title {
	margin: 0;
	font-size: 1.2rem;
	font-weight: 600;
	color: var(--text-color);
}

.modal-message {
	margin: 0;
	color: var(--text-color-secondary);
	line-height: 1.5;
}

.modal-message strong {
	color: var(--text-color);
	font-weight: 600;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.form-group label {
	font-size: 0.82rem;
	font-weight: 600;
	color: var(--text-color);
	letter-spacing: 0.01em;
}

.input-hint {
	margin: 0;
	font-size: 0.85rem;
	color: var(--text-color-secondary);
}

/* --- Form controls ----------------------------------------------------- */
.input {
	width: 100%;
	height: 38px;
	padding: 8px 12px;
	font-size: 0.9rem;
	font-family: inherit;
	color: var(--text-color);
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 6px;
	box-sizing: border-box;
	transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.input:hover:not(:disabled):not(:focus) {
	border-color: var(--text-color-secondary);
}

.input:focus {
	outline: none;
	border-color: var(--noc-blue, #2563eb);
}

.input:disabled {
	background: var(--noc-background-secondary, #f5f5f5);
	color: var(--text-color-secondary);
	cursor: not-allowed;
	opacity: 0.75;
}

.input::placeholder {
	color: var(--text-color-secondary);
	opacity: 0.7;
}

.input.select {
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
	padding-right: 36px;
	background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 12px center;
	background-size: 12px 8px;
	cursor: pointer;
}

.input.select:disabled {
	cursor: not-allowed;
}

.signing-input {
	min-width: 180px;
	flex: 1;
	max-width: 280px;
	width: auto;
}

.signing-date-picker {
	width: 180px;
	max-width: 100%;
}

.supplier-documents-section .signing-controls :is(.signing-input, .secondary-btn),
.signing-date-picker :deep(.drp-trigger) {
	height: var(--signing-control-height);
	min-height: var(--signing-control-height);
	box-sizing: border-box;
}

.signing-date-picker :deep(.drp-trigger) {
	width: 100%;
	padding: 0 12px;
	flex-direction: row-reverse;
	justify-content: space-between;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
	color: var(--text-color);
	font: 400 14px/20px var(--font-family-base);
	box-shadow: none;
}

.signing-date-picker :deep(.drp-trigger:focus-visible) {
	outline: 2px solid var(--noc-blue);
	outline-offset: 2px;
}

.signing-date-picker :deep(.drp-dropdown),
.signing-date-picker :deep(.drp-btn) {
	border-radius: 8px;
}

.secondary-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	height: 34px;
	padding: 0 14px;
	font-size: 0.85rem;
	font-weight: 600;
	color: var(--text-color);
	background: var(--noc-background);
	border: 1px solid var(--border-color);
	border-radius: 6px;
	cursor: pointer;
	transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.secondary-btn:hover:not(:disabled) {
	background: var(--noc-background-secondary, #f5f5f5);
	border-color: var(--text-color-secondary);
}

.secondary-btn:focus-visible {
	outline: none;
	border-color: var(--noc-blue, #2563eb);
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

.secondary-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.supplier-documents-section .input {
	border-radius: 8px;
	font: 400 14px/20px var(--font-family-base);
	transition: none;
}

.supplier-documents-section .input:hover:not(:disabled):not(:focus) {
	border-color: var(--border-color);
	background-color: var(--noc-background-secondary, #fafafb);
}

.supplier-documents-section .input:focus {
	border-color: var(--border-color);
	background-color: var(--noc-background);
	outline: none;
	box-shadow: none;
}

.supplier-documents-section .secondary-btn {
	min-height: 34px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
	color: var(--text-color);
	font: 600 14px/20px var(--font-family-base);
	box-shadow: none;
}

.supplier-documents-section .secondary-btn:hover:not(:disabled),
.supplier-documents-section :is(.secondary-btn, .link-btn):focus-visible,
.documents-toggle:focus-visible {
	border-color: var(--border-color);
	background: var(--noc-background-secondary, #fafafb);
	outline: none;
	box-shadow: none;
	filter: none;
}

.check {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	color: var(--text-color);
	font-size: 0.9rem;
	cursor: pointer;
}

.check input[type='checkbox'] {
	width: 16px;
	height: 16px;
	accent-color: var(--noc-blue, #2563eb);
	cursor: pointer;
}

.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}

.modal-btn {
	border: 1px solid var(--border-color);
	border-radius: 8px;
	padding: 10px 16px;
	font-weight: 600;
	cursor: pointer;
	background: var(--noc-background);
}

.modal-btn.confirm {
	background: var(--noc-blue, #2563eb);
	border-color: var(--noc-blue, #2563eb);
	color: #fff;
}

.modal-btn.danger-confirm {
	background: var(--noc-red, #d9534f);
	border-color: var(--noc-red, #d9534f);
	color: #fff;
}

.modal-btn:hover:not(:disabled),
.modal-btn:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
}

.modal-btn.danger-confirm:hover:not(:disabled),
.modal-btn.danger-confirm:focus-visible {
	background: #b42318;
	border-color: #b42318;
	box-shadow: 0 0 0 3px rgba(180, 35, 24, 0.16);
}

.modal-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

/* --- Responsive -------------------------------------------------------- */
@media (max-width: 840px) {
	.panel-card__header,
	.panel-card__body,
	.panel-card__footer {
		padding-left: 14px;
		padding-right: 14px;
	}

	.panel-card__header {
		flex-wrap: wrap;
	}

	.notice {
		flex-direction: column;
		align-items: stretch;
	}

	.document-group__header {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
	}

	.document-group__identity {
		width: 100%;
		flex: 0 0 auto;
		justify-content: space-between;
	}

	.assign-row {
		width: 100%;
		max-width: none;
		min-width: 0;
		flex: 0 0 auto;
	}

	.file-item {
		flex-direction: column;
		align-items: stretch;
		gap: 4px;
	}

	.file-item__actions {
		justify-content: flex-start;
	}

	.signing-controls {
		width: 100%;
	}

	.signing-field {
		flex: 1 1 220px;
	}

	.signing-input,
	.signing-date-picker {
		width: 100%;
		max-width: none;
	}
}

@media (max-width: 560px) {
	.signing-controls {
		flex-direction: column;
		align-items: stretch;
	}

	.signing-field,
	.signing-controls .secondary-btn {
		width: 100%;
		flex: 0 0 auto;
	}

	.doc-item__actions {
		justify-content: flex-start;
		flex-wrap: wrap;
	}
}

@media (any-pointer: coarse) {
	.signing-date-picker :deep(.drp-trigger),
	.signing-date-picker :deep(.drp-cell),
	.signing-date-picker :deep(.drp-btn),
	.signing-date-picker :deep(.drp-nav-btn),
	.signing-date-picker :deep(.drp-month-title) {
		touch-action: manipulation;
	}
}

@media (prefers-reduced-motion: reduce) {
	.document-action-btn,
	.documents-toggle__label::after,
	.documents-toggle__chevron,
	.doc-item__toggle,
	.doc-item__chevron,
	.doc-details-enter-active,
	.doc-details-leave-active {
		transition: none;
	}
}
</style>
